// components/admin-dashboard/charts/CounselorPerformanceChart.js
import React, { useState, useEffect } from "react";
import { ResponsiveContainer, BarChart, CartesianGrid, XAxis, YAxis, Tooltip, Legend, Bar } from "recharts";
import { BarChart3, ChevronDown, User, X } from "lucide-react";
import { ChartCard } from "./ChartCard";
import { CustomTooltip } from "./CustomTooltip";
import { COLORS } from '../constants';
import { CounselorDetailView } from '../../hod-dashboard/components/CounselorDetailView';
import useApi from '../../../../hooks/useApi';

export const CounselorPerformanceChart = ({ data, isLoading, counselors = [] }) => {
  const [selectedId, setSelectedId] = useState("");
  const [detailData, setDetailData] = useState(null);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const { get } = useApi();

  useEffect(() => {
    const fetchDetail = async () => {
      if (!selectedId) {
        setDetailData(null);
        return;
      }
      setIsDetailLoading(true);
      try {
        const response = await get(`/dashboard/counselors/performance`, { 
          params: { counselorId: selectedId } 
        });
        setDetailData(response.data.data);
      } catch (error) {
        console.error("Error fetching counselor detail:", error);
      } finally {
        setIsDetailLoading(false);
      }
    };
    fetchDetail();
  }, [selectedId, get]);

  return (
    <ChartCard 
      title={selectedId ? "Counselor Detail" : "Counselor Performance"} 
      icon={BarChart3} 
      isLoading={isLoading}
    >
      <div className="flex flex-col space-y-4">
        {/* simplistic add on to the chart itself */}
        <div className="flex items-center justify-between bg-gray-50 dark:bg-slate-800/50 p-2 rounded-xl border border-gray-100 dark:border-gray-800">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-400">
            <User className="h-4 w-4" />
            <span>Select Focus:</span>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              className="bg-transparent text-sm font-bold text-blue-600 dark:text-blue-400 focus:outline-none cursor-pointer"
            >
              <option value="">Compare All Counselors</option>
              {counselors.map(c => (
                <option key={c._id} value={c._id}>{c.fullName}</option>
              ))}
            </select>
            {selectedId && (
              <button 
                onClick={() => setSelectedId("")}
                className="p-1 hover:bg-gray-200 dark:hover:bg-slate-700 rounded-full transition-colors"
                title="Clear selection"
              >
                <X className="h-3 w-3 text-gray-400" />
              </button>
            )}
          </div>
        </div>

        <div className={selectedId ? "" : "h-[300px]"}>
          {selectedId ? (
            <CounselorDetailView data={detailData} isLoading={isDetailLoading} />
          ) : (
            <ResponsiveContainer>
              <BarChart data={data}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                <XAxis
                  dataKey="name"
                  tickFormatter={(value) => value.split(" ")[0]}
                  axisLine={false}
                  tickLine={false}
                  tick={{fill: '#64748B', fontSize: 12}}
                />
                <YAxis axisLine={false} tickLine={false} tick={{fill: '#64748B', fontSize: 12}} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar
                  dataKey="completedSessions"
                  fill={COLORS.primary}
                  name="Completed Sessions"
                  radius={[4, 4, 0, 0]}
                />
                <Bar
                  dataKey="totalAppointments"
                  fill={COLORS.secondary}
                  name="Bookings"
                  radius={[4, 4, 0, 0]}
                />
                <Bar
                  dataKey="totalUniqueClients"
                  fill={COLORS.accent}
                  name="Unique Clients"
                  radius={[4, 4, 0, 0]}
                />
                <Bar
                  dataKey="retentionRate"
                  hide
                  name="Retention Rate"
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </ChartCard>
  );
};